/**
 * Account Backup - Encrypted export/import of identity
 *
 * Uses PBKDF2 for key derivation and AES-256-GCM for encryption.
 * Format: aos1:<base64-salt>:<base64-iv>:<base64-ciphertext>
 */

import { base58 } from './base58.js';
import type { KeyPair } from './keys.js';

// Backup format version
const BACKUP_VERSION = 'aos1';

// PBKDF2 parameters
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;

/**
 * Backup payload structure
 */
export interface BackupPayload {
  version: 1;
  /** Private key as base58 */
  privateKey: string;
  /** Public key as base58 */
  publicKey: string;
  /** Optional profile DAG hash */
  profileHash?: string;
  /** Optional settings */
  settings?: Record<string, unknown>;
  /** Export timestamp */
  exportedAt: number;
}

/**
 * Encrypt a keypair with a password
 *
 * @param keyPair - The keypair to encrypt
 * @param password - User-provided password
 * @param options - Optional profile hash and settings to include
 * @returns Encrypted backup string in format: aos1:salt:iv:ciphertext
 */
export async function encryptBackup(
  keyPair: KeyPair,
  password: string,
  options?: {
    profileHash?: string;
    settings?: Record<string, unknown>;
  }
): Promise<string> {
  // Create the payload
  const payload: BackupPayload = {
    version: 1,
    privateKey: base58.encode(keyPair.privateKey),
    publicKey: keyPair.publicKeyBase58,
    profileHash: options?.profileHash,
    settings: options?.settings,
    exportedAt: Date.now(),
  };

  const payloadJson = JSON.stringify(payload);
  const payloadBytes = new TextEncoder().encode(payloadJson);

  // Generate salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Derive key from password using PBKDF2
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const aesKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );

  // Encrypt the payload
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    payloadBytes
  );

  // Encode as base64 and combine
  const saltB64 = arrayBufferToBase64(salt);
  const ivB64 = arrayBufferToBase64(iv);
  const ciphertextB64 = arrayBufferToBase64(new Uint8Array(ciphertext));

  return `${BACKUP_VERSION}:${saltB64}:${ivB64}:${ciphertextB64}`;
}

/**
 * Decrypt a backup string with a password
 *
 * @param backup - Encrypted backup string
 * @param password - User-provided password
 * @returns Decrypted backup payload
 * @throws Error if backup is invalid or password is wrong
 */
export async function decryptBackup(
  backup: string,
  password: string
): Promise<BackupPayload> {
  // Parse the backup string
  const parts = backup.trim().split(':');
  if (parts.length !== 4) {
    throw new Error('Invalid backup format: expected 4 parts separated by colons');
  }

  const [version, saltB64, ivB64, ciphertextB64] = parts;

  if (version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version: ${version}`);
  }

  // Decode from base64
  const salt = base64ToArrayBuffer(saltB64);
  const iv = base64ToArrayBuffer(ivB64);
  const ciphertext = base64ToArrayBuffer(ciphertextB64);

  // Derive key from password
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const aesKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  // Decrypt
  let payloadBytes: ArrayBuffer;
  try {
    payloadBytes = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      ciphertext
    );
  } catch {
    throw new Error('Decryption failed: incorrect password or corrupted backup');
  }

  // Parse payload
  const payloadJson = new TextDecoder().decode(payloadBytes);
  let payload: BackupPayload;
  try {
    payload = JSON.parse(payloadJson);
  } catch {
    throw new Error('Invalid backup: corrupted payload');
  }

  // Validate payload
  if (payload.version !== 1) {
    throw new Error(`Unsupported payload version: ${payload.version}`);
  }
  if (!payload.privateKey || !payload.publicKey) {
    throw new Error('Invalid backup: missing keys');
  }

  return payload;
}

/**
 * Restore a keypair from a backup payload
 */
export function restoreKeyPair(payload: BackupPayload): KeyPair {
  const privateKey = base58.decode(payload.privateKey);
  const publicKey = base58.decode(payload.publicKey);

  return {
    privateKey,
    publicKey,
    publicKeyBase58: payload.publicKey,
  };
}

/**
 * Validate a backup string format (without decrypting)
 */
export function validateBackupFormat(backup: string): {
  valid: boolean;
  error?: string;
} {
  const parts = backup.trim().split(':');

  if (parts.length !== 4) {
    return { valid: false, error: 'Invalid format: expected 4 parts' };
  }

  const [version, saltB64, ivB64, ciphertextB64] = parts;

  if (version !== BACKUP_VERSION) {
    return { valid: false, error: `Unsupported version: ${version}` };
  }

  try {
    const salt = base64ToArrayBuffer(saltB64);
    if (salt.byteLength !== SALT_LENGTH) {
      return { valid: false, error: 'Invalid salt length' };
    }
  } catch {
    return { valid: false, error: 'Invalid salt encoding' };
  }

  try {
    const iv = base64ToArrayBuffer(ivB64);
    if (iv.byteLength !== IV_LENGTH) {
      return { valid: false, error: 'Invalid IV length' };
    }
  } catch {
    return { valid: false, error: 'Invalid IV encoding' };
  }

  try {
    base64ToArrayBuffer(ciphertextB64);
  } catch {
    return { valid: false, error: 'Invalid ciphertext encoding' };
  }

  return { valid: true };
}

// ============================================================================
// Helpers
// ============================================================================

function arrayBufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}
